import React, { useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Loader2, Sparkles, CheckCircle2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import PipelineStepper from "@/components/simulator/PipelineStepper";
import StepFeedback from "@/components/simulator/StepFeedback";
import ConnectStep from "@/components/simulator/ConnectStep";
import StreamsStep from "@/components/simulator/StreamsStep";
import MappingStep from "@/components/simulator/MappingStep";
import IdentityStep from "@/components/simulator/IdentityStep";
import UnifiedProfileStep from "@/components/simulator/UnifiedProfileStep";
import SegmentInsightStep from "@/components/simulator/SegmentInsightStep";
import ActivateStep from "@/components/simulator/ActivateStep";

const STEPS = ["connect", "streams", "mapping", "identity", "unified", "segment_insight", "activate"];

const emptyAnswers = () => ({
  connect: [], streams: {}, mapping: {}, identity: [],
  segment_insight: { segment: {}, insight_id: null }, activate: { channel_id: null },
});

function seedFromCompleted(stepsCompleted) {
  const answers = emptyAnswers();
  const results = {};
  for (const step of STEPS) {
    const done = stepsCompleted[step];
    if (!done) continue;
    const ans = done.answer || {};
    if (step === "connect") answers.connect = ans.selected_sources || [];
    else if (step === "streams") answers.streams = ans.stream_choices || {};
    else if (step === "mapping") answers.mapping = ans.mapping || {};
    else if (step === "identity") answers.identity = ans.selected_rules || [];
    else if (step === "segment_insight") answers.segment_insight = { segment: ans.segment || {}, insight_id: ans.insight_id || null };
    else if (step === "activate") answers.activate = { channel_id: ans.channel_id || null };
    results[step] = { score: done.score, ai_feedback: done.ai_feedback, is_correct: done.score >= 80 };
  }
  return { answers, results };
}

export default function CaseStudySimulatorTab({ caseStudyId, scenario, initialSteps, onAllDone, disabled, apiPrefix = "/case-studies", terms = {}, stepMeta }) {
  const seeded = seedFromCompleted(initialSteps || {});
  const [stepIndex, setStepIndex] = useState(0);
  const [answers, setAnswers] = useState(seeded.answers);
  const [results, setResults] = useState(seeded.results);
  const [checking, setChecking] = useState(false);

  const currentStep = STEPS[stepIndex];
  const result = results[currentStep];
  const isLast = stepIndex === STEPS.length - 1;

  const buildPayload = () => {
    if (currentStep === "connect") return { selected_sources: answers.connect };
    if (currentStep === "streams") return { stream_choices: answers.streams };
    if (currentStep === "mapping") return { mapping: answers.mapping };
    if (currentStep === "identity") return { selected_rules: answers.identity };
    if (currentStep === "unified") return {};
    if (currentStep === "segment_insight") return { segment: answers.segment_insight.segment, insight_id: answers.segment_insight.insight_id };
    if (currentStep === "activate") return { channel_id: answers.activate.channel_id };
    return {};
  };

  const checkStep = async () => {
    setChecking(true);
    try {
      const { data } = await api.post(`${apiPrefix}/${caseStudyId}/simulator/steps/${currentStep}`, buildPayload());
      const nextResults = { ...results, [currentStep]: data };
      setResults(nextResults);
      if (STEPS.every((s) => !!nextResults[s])) onAllDone(true);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to check this step");
    } finally {
      setChecking(false);
    }
  };

  const connectedNames = answers.connect.map((id) => scenario.sources.find((s) => s.id === id)?.name).filter(Boolean);
  const identityLabels = answers.identity.map((id) => scenario.identity_rule_options.find((o) => o.id === id)?.label).filter(Boolean);

  return (
    <div data-testid="case-study-simulator-tab">
      <PipelineStepper steps={STEPS} currentStep={currentStep} results={results} onJump={setStepIndex} stepMeta={stepMeta} />

      <div className="glass-panel rounded-2xl p-6">
        {currentStep === "connect" && (
          <ConnectStep sources={scenario.sources} selected={answers.connect} onChange={(v) => setAnswers((p) => ({ ...p, connect: v }))} />
        )}
        {currentStep === "streams" && (
          <StreamsStep sources={scenario.sources} connectedIds={answers.connect} streamOptions={scenario.stream_options} choices={answers.streams} onChange={(v) => setAnswers((p) => ({ ...p, streams: v }))} title={terms.streamsTitle} description={terms.streamsDescription} />
        )}
        {currentStep === "mapping" && (
          <MappingStep sources={scenario.sources} connectedIds={answers.connect} mappableFields={scenario.mappable_fields} targetObjects={scenario.target_objects} mapping={answers.mapping} onChange={(v) => setAnswers((p) => ({ ...p, mapping: v }))} title={terms.mappingTitle} description={terms.mappingDescription} targetSectionLabel={terms.mappingTargetSectionLabel} />
        )}
        {currentStep === "identity" && (
          <IdentityStep options={scenario.identity_rule_options} selected={answers.identity} onChange={(v) => setAnswers((p) => ({ ...p, identity: v }))} title={terms.identityTitle} description={terms.identityDescription} />
        )}
        {currentStep === "unified" && (
          <UnifiedProfileStep connectedNames={connectedNames} mappedCount={Object.keys(answers.mapping).length} identityLabels={identityLabels} title={terms.unifiedTitle} description={terms.unifiedDescription} entityLabel={terms.unifiedEntityLabel} fieldUnitLabel={terms.unifiedFieldUnitLabel} />
        )}
        {currentStep === "segment_insight" && (
          <SegmentInsightStep
            segmentDef={scenario.segment} insightDef={scenario.insight}
            segment={answers.segment_insight.segment} onSegmentChange={(v) => setAnswers((p) => ({ ...p, segment_insight: { ...p.segment_insight, segment: v } }))}
            insightId={answers.segment_insight.insight_id} onInsightChange={(v) => setAnswers((p) => ({ ...p, segment_insight: { ...p.segment_insight, insight_id: v } }))}
            segmentStepTitle={terms.segmentStepTitle} insightStepTitle={terms.insightStepTitle}
          />
        )}
        {currentStep === "activate" && (
          <ActivateStep
            channels={scenario.activation.channels} channelId={answers.activate.channel_id} onChange={(v) => setAnswers((p) => ({ ...p, activate: { channel_id: v } }))}
            summary={{ connectedCount: answers.connect.length, streamsCount: Object.keys(answers.streams).length, mappedCount: Object.keys(answers.mapping).length, identityCount: answers.identity.length }}
            title={terms.activateTitle} description={terms.activateDescription}
          />
        )}

        <StepFeedback result={result} />

        <div className="flex items-center justify-between mt-6 pt-6 border-t border-white/5">
          <button
            onClick={() => setStepIndex((i) => Math.max(0, i - 1))} disabled={stepIndex === 0} data-testid="cs-sim-prev-btn"
            className="flex items-center gap-1.5 text-sm font-semibold text-slate-400 hover:text-white disabled:opacity-30 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" /> Previous
          </button>
          {!disabled && (
            <button
              onClick={checkStep} disabled={checking} data-testid="cs-sim-check-btn"
              className="flex items-center gap-2 bg-white/10 hover:bg-white/15 disabled:opacity-60 text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors"
            >
              {checking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {result ? "Re-check with AI" : "Check My Work"}
            </button>
          )}
          {!isLast ? (
            <button
              onClick={() => setStepIndex((i) => Math.min(STEPS.length - 1, i + 1))} data-testid="cs-sim-next-btn"
              className="flex items-center gap-1.5 text-sm font-semibold text-slate-400 hover:text-white transition-colors"
            >
              Next <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            STEPS.every((s) => !!results[s]) && (
              <span className="flex items-center gap-1.5 text-sm font-semibold text-emerald-400" data-testid="cs-sim-all-done">
                <CheckCircle2 className="w-4 h-4" /> All steps checked
              </span>
            )
          )}
        </div>
      </div>
    </div>
  );
}
